import { useState, useEffect, useMemo, useRef } from 'react';
import { useFitHeight } from '../hooks/useFitHeight';
import {
  collection, doc, addDoc, updateDoc, setDoc,
  onSnapshot, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import { useSettings } from '../hooks/useSettings';
import { isIndian, invoiceGst, gstConflict, paidGstPosting, gstPercentsOf, type GstInput } from '../utils/tradingSystem';
import { PaidGstFields } from '../components/GstBoxes';
import PayGstModal from '../components/PayGstModal';
import AppLayout from '../components/AppLayout';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSalePopup } from '../components/SalePopup';
import AdvanceDetailModal from '../components/AdvanceDetailModal';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, Dropdown, DeleteIconButton } from '../components/ui';
import BankMoveModal, { type BankMoveKind } from '../components/BankMoveModal';
import BankPaymentPicker, { type BankPaymentKind } from '../components/BankPaymentPicker';
import { CURRENCY_LIST, getCurrencySymbol, fmtMoney } from '../utils/currency';
import {
  EXPENSE_CATEGORIES, INCOME_CATEGORIES, bankAccountSlug,
  buildEntry, postEntry, entryId, linesExpense, linesIncome, linesOpeningBank,
} from '../utils/ledger';
import type { BankAccount, BankAccountType, BankTransaction, Entity, SaleInvoice, PurchaseInvoice, CustomerAdvance, SupplierAdvance } from '../types';
import { shortInvoiceNo } from '../utils/invoiceNo';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

const now = new Date();

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== '')) as T;
}

// ── Page size — personal, per-browser preference (never BusinessSettings) ──
const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> transaction{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Forms ─────────────────────────────────────────────────────────────────────

interface AccountForm {
  name: string; type: BankAccountType; currency: string;
  bankName: string; accountNumber: string; ifscCode: string;
  iban: string; swiftCode: string; openingBalance: string; notes: string;
}

function emptyAccountForm(defaultCurrency: string): AccountForm {
  return {
    name: '', type: 'cash', currency: defaultCurrency,
    bankName: '', accountNumber: '', ifscCode: '', iban: '', swiftCode: '',
    openingBalance: '0', notes: '',
  };
}

interface EntryForm {
  direction: 'out' | 'in';
  accountId: string;
  category: string;
  amount: string;
  date: string;
  description: string;
  referenceNo: string;
  notes: string;
}

function emptyEntryForm(direction: 'out' | 'in', defaultAccountId: string): EntryForm {
  return {
    direction, accountId: defaultAccountId,
    category: '', amount: '', date: todayISO(),
    description: '', referenceNo: '', notes: '',
  };
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconPlus() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
}
function IconEdit() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>;
}
function IconTrash() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3v1M4 3.5l.667 8h4.666L10 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function IconBank({ size = 20 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 20 20" fill="none"><rect x="2" y="8" width="16" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" /><path d="M2 8l8-5 8 5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M5 11.5v3.5M10 11.5v3.5M15 11.5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
}
function IconCash({ size = 20 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 20 20" fill="none"><rect x="1.5" y="5" width="17" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" /><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" /><path d="M5 10h1M14 10h1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>;
}
function IconTransactions({ size = 20 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
    <rect x="3" y="2" width="14" height="16" rx="1.8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M6.5 6.5h7M6.5 10h7M6.5 13.5h4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconArrowOut() {
  return <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v7M3 5l3-4 3 4M2 11h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function IconArrowIn() {
  return <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 11V4M3 7l3 4 3-4M2 1h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

// ── Transactions filter bar bits ─────────────────────────────────────────────

// A row's type in words, the same words as its badge (TxTypeBadge). Used
// by the Type column's Excel-style filter.
function txTypeName(tx: BankTransaction): string {
  const t = tx.referenceType as string;
  if (t === 'ap_payment') return 'AP Payment';
  if (t === 'ar_payment') return 'AR Receipt';
  if (t === 'opening') return 'Opening';
  if (t === 'supplier_advance') return 'Advance Out';
  if (t === 'customer_advance') return 'Advance In';
  if (t === 'expense' || (t === 'manual' && tx.direction === 'out')) return 'Expense';
  if (t === 'purchase_expense') return 'Purchase Expense';
  if (t === 'sale_expense') return 'Sales Expense';
  if (t === 'broker_payment') return 'Broker Payout';
  if (t === 'income' || (t === 'manual' && tx.direction === 'in')) return 'Income';
  if (t === 'transfer') return 'Transfer';
  if (t === 'deposit') return 'Deposit';
  if (t === 'withdrawal') return 'Withdrawal';
  if (t === 'bank_charge') return 'Bank Charge';
  if (t === 'gst_payment') return 'GST Paid';
  return '';
}
// Local calendar day — never toISOString() (UTC); see CLAUDE.md "Date-only strings".
function localTodayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function shiftDay(day: string, n: number) {
  const d = new Date(day + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const MONTH_STEP_BTN: React.CSSProperties = {
  width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent', border: 'none', color: 'var(--text-2)', cursor: 'pointer', padding: 0,
};
function IconChevron({ dir }: { dir: 'left' | 'right' }) {
  return <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d={dir === 'left' ? 'M7.5 2.5L4 6l3.5 3.5' : 'M4.5 2.5L8 6 4.5 9.5'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCaret() {
  return <svg width="10" height="10" viewBox="0 0 10 6" fill="none" style={{ marginLeft: 2, opacity: 0.6, flexShrink: 0 }}>
    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCalendar() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.5 5.5h11M4.5 1v3M9.5 1v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}

// ── Month / day picker (opens from the month label) ─────────────────────────
// Browse months with its own arrows (they stopPropagation so the Dropdown
// stays open), click a day to show just that day (closes), or "Whole month"
// to show the browsed month. Future days are disabled; days with
// transactions carry a dot.
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function MonthDayPicker({ year, month, day, txDays, onPickDay, onPickMonth }: {
  year: number; month: number; day: string; txDays: Set<string>;
  onPickDay: (day: string) => void;
  onPickMonth: (year: number, month: number) => void;
}) {
  const [viewY, setViewY] = useState(year);
  const [viewM, setViewM] = useState(month);
  const today = localTodayISO();
  const pad = (n: number) => String(n).padStart(2, '0');
  const isFutureView = viewY > now.getFullYear() || (viewY === now.getFullYear() && viewM >= now.getMonth());

  const first = new Date(viewY, viewM, 1);
  const lead = (first.getDay() + 6) % 7;                 // Monday-first
  const daysIn = new Date(viewY, viewM + 1, 0).getDate();
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysIn }, (_, i) => `${viewY}-${pad(viewM + 1)}-${pad(i + 1)}`),
  ];

  function step(e: React.MouseEvent, n: number) {
    e.stopPropagation();
    const d = new Date(viewY, viewM + n, 1);
    setViewY(d.getFullYear()); setViewM(d.getMonth());
  }
  const navBtn: React.CSSProperties = { ...MONTH_STEP_BTN, width: 28, height: 28, borderRadius: 6 };

  return (
    <div style={{ padding: 10, width: 252 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button type="button" style={navBtn} onClick={e => step(e, -1)} title="Previous month"><IconChevron dir="left" /></button>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{MONTH_NAMES[viewM]} {viewY}</span>
        <button type="button" style={{ ...navBtn, opacity: isFutureView ? 0.3 : 1, cursor: isFutureView ? 'default' : 'pointer' }}
          disabled={isFutureView} onClick={e => step(e, 1)} title="Next month"><IconChevron dir="right" /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {WEEKDAYS.map(w => (
          <div key={w} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textAlign: 'center', padding: '2px 0 4px' }}>{w}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} />;
          const future = d > today;
          const selected = d === day;
          const isToday = d === today;
          return (
            <button
              key={d} type="button" disabled={future}
              onClick={e => { if (future) { e.stopPropagation(); return; } onPickDay(d); }}
              style={{
                position: 'relative', height: 30, borderRadius: 6, fontFamily: 'inherit', fontSize: 12,
                cursor: future ? 'default' : 'pointer',
                border: isToday && !selected ? '1.5px solid var(--primary)' : '1.5px solid transparent',
                background: selected ? 'var(--primary)' : 'transparent',
                color: selected ? '#fff' : future ? 'var(--text-3)' : 'var(--text-1)',
                opacity: future ? 0.4 : 1, fontWeight: selected || isToday ? 700 : 500,
              }}
            >
              {Number(d.slice(8))}
              {txDays.has(d) && !selected && (
                <span style={{ position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: 'var(--primary)' }} />
              )}
            </button>
          );
        })}
      </div>
      <button
        type="button" onClick={() => onPickMonth(viewY, viewM)}
        style={{
          width: '100%', marginTop: 8, height: 30, borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 12, fontWeight: 600, border: '1px solid var(--border)',
          background: !day && viewY === year && viewM === month ? 'var(--primary-bg)' : 'var(--surface-2)',
          color: 'var(--text-2)',
        }}
      >
        Whole month — {MONTH_NAMES[viewM]} {viewY}
      </button>
    </div>
  );
}

// ── TxTypeBadge ───────────────────────────────────────────────────────────────

function TxTypeBadge({ tx }: { tx: BankTransaction }) {
  const { referenceType, direction } = tx;
  if (referenceType === 'ap_payment') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid color-mix(in srgb, var(--primary) 25%, transparent)', whiteSpace: 'nowrap' }}>AP Payment</span>
  );
  if (referenceType === 'ar_payment') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)', whiteSpace: 'nowrap' }}>AR Receipt</span>
  );
  if (referenceType === 'opening') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--surface-2)', color: 'var(--text-3)', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Opening</span>
  );
  if (referenceType === 'supplier_advance') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid color-mix(in srgb, var(--primary) 25%, transparent)', whiteSpace: 'nowrap' }}>Advance Out</span>
  );
  if (referenceType === 'customer_advance') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)', whiteSpace: 'nowrap' }}>Advance In</span>
  );
  if (referenceType === 'expense' || (referenceType as string) === 'manual' && direction === 'out') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)', whiteSpace: 'nowrap' }}>Expense</span>
  );
  if (referenceType === 'purchase_expense') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Purchase Expense</span>
  );
  if (referenceType === 'sale_expense') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Sales Expense</span>
  );
  if ((referenceType as string) === 'gst_payment') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>GST Paid</span>
  );
  if (referenceType === 'broker_payment') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Broker Payout</span>
  );
  if (referenceType === 'income' || (referenceType as string) === 'manual' && direction === 'in') return (
    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)', whiteSpace: 'nowrap' }}>Income</span>
  );
  return null;
}

// ── AddEntryModal ─────────────────────────────────────────────────────────────

function AddEntryModal({ open, onClose, accounts, defaultCurrency, userId, businessId }: {
  open: boolean; onClose: () => void;
  accounts: BankAccount[]; defaultCurrency: string;
  userId: string; businessId: string;
}) {
  const { user } = useAuth();
  const settings = useSettings();
  const defaultAcc = accounts.find(a => a.isDefault)?.id ?? accounts[0]?.id ?? '';
  const [form,   setForm]   = useState<EntryForm>(() => emptyEntryForm('out', defaultAcc));
  const [saving, setSaving] = useState(false);
  // Indian trading system: GST on an expense paid from the bank.
  const indian = isIndian(settings);
  const [gstIn, setGstIn] = useState<GstInput>({ cgst: '', sgst: '', igst: '' });
  const [gstNotClaimable, setGstNotClaimable] = useState(false);
  const [gstReverse, setGstReverse] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    if (open) {
      const acc = accounts.find(a => a.isDefault)?.id ?? accounts[0]?.id ?? '';
      setForm(emptyEntryForm('out', acc));
      setGstIn({ cgst: '', sgst: '', igst: '' }); setGstNotClaimable(false); setGstReverse(false);
      setError('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function set<K extends keyof EntryForm>(k: K, v: EntryForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function switchDirection(d: 'out' | 'in') {
    setForm(f => ({ ...f, direction: d, category: '' }));
  }

  const selectedAcc = accounts.find(a => a.id === form.accountId);
  const currency    = selectedAcc?.currency ?? defaultCurrency;
  const sym         = getCurrencySymbol(currency);
  const categories  = form.direction === 'out' ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;

  function validate(): string {
    if (!form.accountId)                          return 'Select an account.';
    if (!form.category)                           return 'Select a category.';
    if (!form.description.trim())                 return 'Enter a description.';
    const amt = parseFloat(form.amount);
    if (!form.amount || isNaN(amt) || amt <= 0)  return 'Enter a valid amount.';
    if (!form.date)                               return 'Select a date.';
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }

    // Indian expense: the box is the amount before GST; the bank pays it
    // plus GST (not on reverse charge, where we owe the GST ourselves).
    const baseAmt = parseFloat(form.amount);
    const gstOn   = indian && form.direction === 'out';
    const gstPcts = gstOn ? gstPercentsOf(gstIn) : {};
    if (gstOn && gstConflict(gstPcts)) { setError('Use CGST + SGST, or IGST, not both.'); setSaving(false); return; }
    const gstTotal = gstOn ? invoiceGst(baseAmt, gstPcts).total : 0;
    const amt    = gstTotal > 0 && !gstReverse ? Math.round((baseAmt + gstTotal) * 100) / 100 : baseAmt;
    const gstFields = gstTotal > 0
      ? { gst: { cgstPct: gstPcts.cgstPct ?? null, sgstPct: gstPcts.sgstPct ?? null, igstPct: gstPcts.igstPct ?? null },
          gstTotalUSD: gstTotal, gstNotClaimable: gstNotClaimable || undefined, gstReverseCharge: gstReverse || undefined }
      : {};
    const accRef = doc(db, 'bankAccounts', form.accountId);
    const txRef  = doc(collection(db, 'bankTransactions'));
    const nowISO = new Date().toISOString();

    try {
      await runTransaction(db, async tx => {
        const accSnap = await tx.get(accRef);
        if (!accSnap.exists()) throw new Error('Account not found.');
        const curBal  = (accSnap.data() as BankAccount).currentBalance;
        const newBal  = form.direction === 'out' ? curBal - amt : curBal + amt;

        tx.set(txRef, stripUndef({
          businessId,
          accountId:     form.accountId,
          accountName:   selectedAcc!.name,
          currency,
          direction:     form.direction,
          amount:        amt,
          date:          form.date,
          description:   form.description.trim(),
          referenceType: form.direction === 'out' ? 'expense' : 'income',
          category:      form.category,
          referenceNo:   form.referenceNo.trim() || undefined,
          notes:         form.notes.trim()        || undefined,
          ...gstFields,
          createdAt:     nowISO,
          createdBy:     userId,
        }));        logActivity(tx, db, businessId, user, {
          module: 'bank', action: 'create', recordId: txRef.id,
          note: `${form.direction === 'out' ? 'Expense' : 'Income'}: ${form.description.trim() || form.category} · ${fmtMoney(amt, currency)} · ${selectedAcc!.name}`,
        });


        tx.update(accRef, { currentBalance: newBal });

        // Ledger (additive) — a standalone cash-book expense/income entry,
        // keyed by the bank-transaction id (no linked `expenses` doc).
        postEntry(tx, db, buildEntry({
          businessId, sourceType: form.direction === 'out' ? 'expense' : 'income',
          sourceId: txRef.id, sourceCollection: 'bankTransactions',
          date: form.date, currency, createdBy: userId,
          note: form.description.trim(),
          lines: form.direction === 'out'
            ? linesExpense(amt, form.category, { bankAccountId: form.accountId }, undefined, paidGstPosting(gstFields))
            : linesIncome(amt, form.accountId),
        }), entryId(form.direction === 'out' ? 'expense' : 'income', txRef.id));
      });

      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      hideHead
      size="md"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              variant={form.direction === 'out' ? 'danger' : 'success'}
              onClick={handleSave} loading={saving}
            >
              {form.direction === 'out' ? 'Record Expense' : 'Record Income'}
            </Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={form.category || 'Add Entry'}
        eyebrow={form.direction === 'out' ? 'Record Expense' : 'Record Income'}
        eyebrowColor={form.direction === 'out' ? 'var(--danger)' : 'var(--success)'}
        onClose={onClose}
      />

      {/* Expense / Income toggle */}
      <div style={{ display: 'flex', marginBottom: 20, borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--border)' }}>
        {(['out', 'in'] as const).map(d => (
          <button key={d} type="button" onClick={() => switchDirection(d)} style={{
            flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            background: form.direction === d
              ? (d === 'out' ? 'var(--danger)' : 'var(--success)')
              : 'var(--surface)',
            color: form.direction === d ? '#fff' : 'var(--text-3)',
            transition: 'background 0.15s, color 0.15s',
          }}>
            {d === 'out' ? <><IconArrowOut /> Expense</> : <><IconArrowIn /> Income</>}
          </button>
        ))}
      </div>

      <div className="form-row-2">
        <FormField label="Account" required>
          <select value={form.accountId} onChange={e => set('accountId', e.target.value)}>
            <option value="">Select account…</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Category" required>
          <select value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="">Select category…</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
      </div>

      <FormField label="Description" required>
        <input value={form.description} onChange={e => set('description', e.target.value)}
          placeholder={form.direction === 'out' ? 'e.g. Office rent — July 2025' : 'e.g. Interest from HDFC Bank'} />
      </FormField>

      <div className="form-row-2">
        <FormField label={indian && form.direction === 'out' ? `Amount before GST (${currency})` : `Amount (${currency})`} required>
          <div className="input-wrap has-prefix">
            <span className="input-prefix">{sym}</span>
            <input type="number" step="0.01" min="0.01" value={form.amount}
              onChange={e => set('amount', e.target.value)} placeholder="0.00" autoFocus />
          </div>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
        </FormField>
      </div>
      {indian && form.direction === 'out' && (
        <PaidGstFields base={parseFloat(form.amount) || 0} value={gstIn} onChange={setGstIn}
          notClaimable={gstNotClaimable} onNotClaimable={setGstNotClaimable}
          reverseCharge={gstReverse} onReverseCharge={setGstReverse}
          money={v => fmtMoney(v, currency)} />
      )}

      <div className="form-row-2">
        <FormField label="Reference No." hint="Cheque no., receipt no., etc.">
          <input value={form.referenceNo} onChange={e => set('referenceNo', e.target.value)}
            placeholder="Optional" style={{ fontFamily: 'var(--font-num)' }} />
        </FormField>
        <FormField label="Notes (optional)">
          <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />
        </FormField>
      </div>
    </Modal>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

// ── Summary bar ──────────────────────────────────────────────────────────────
// Total balance per currency (never summed across currencies), the selected
// month's In / Out / Net for whatever the Transactions filters are set to,
// and one compact card per active account.

/** "BE68539007547034" → "BE68 5390 0754 7034" (easier to read and check). */
function groupChars(v: string) {
  return v.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

function BankSummaryBar({
  accounts, loading, balancesByCurrency, monthLabel, flowByAccount, txLoading,
  activeAccountId, onPickAccount, onAddAccount, collapsed, onToggle,
}: {
  accounts: BankAccount[];
  loading: boolean;
  balancesByCurrency: Record<string, number>;
  monthLabel: string;
  /** This period's money in / out per account (its own currency). */
  flowByAccount: Record<string, { in: number; out: number }>;
  txLoading: boolean;
  activeAccountId: string;
  onPickAccount: (id: string) => void;
  onAddAccount: () => void;
  /** Folded to one slim line (more room for the table). */
  collapsed: boolean;
  onToggle: () => void;
}) {
  const currencies = Object.keys(balancesByCurrency).sort();

  if (loading) {
    return <div className="card" style={{ marginBottom: 16 }}><div className="skeleton" style={{ height: 110, borderRadius: 8 }} /></div>;
  }
  if (accounts.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px' }}>
        <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconBank size={26} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>No bank or cash accounts yet</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Add your Cash account and bank accounts to start recording money in and out.</div>
        </div>
        <Button variant="primary" size="sm" icon={<IconPlus />} onClick={onAddAccount}>Add Account</Button>
      </div>
    );
  }

  const toggle = (
    <button type="button" className="bank-summary-toggle" onClick={onToggle}
      title={collapsed ? `Show accounts (${accounts.length})` : 'Hide accounts'}
      aria-label={collapsed ? `Show accounts (${accounts.length})` : 'Hide accounts'} aria-expanded={!collapsed}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: collapsed ? undefined : 'rotate(180deg)', transition: 'transform 0.15s' }}>
        <path d="M2 3.5L5 6.5l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
  const totalsLine = (
    <div className="bank-summary-totals">
      {currencies.map(c => {
        const v = balancesByCurrency[c];
        return (
          <span key={c} className={`bank-total${v === 0 ? ' zero' : v < 0 ? ' neg' : ''}`}>
            <span className="bank-total-cur">{c}</span>
            {fmtMoney(v, c)}
          </span>
        );
      })}
    </div>
  );

  if (collapsed) {
    return (
      <div className="card bank-summary bank-summary-mini">
        <span className="bank-summary-label bank-summary-label-row" style={{ margin: 0 }}>Total balance{toggle}</span>
        {totalsLine}
      </div>
    );
  }

  return (
    <div className="card bank-summary">
      {/* Row 1: total per currency (side by side). Zero totals are grey so
          real balances stand out. This period's in / out sits in each
          account's own card below (its own currency). */}
      <div className="bank-summary-top">
        <div className="bank-summary-block">
          <div className="bank-summary-label bank-summary-label-row">Total balance{toggle}</div>
          {totalsLine}
        </div>
      </div>

      {/* Row 2: one card per account. Click to show only its transactions. */}
      <div className="bank-acc-grid">
        {accounts.map(acc => {
          const active = activeAccountId === acc.id;
          const isCash = acc.type === 'cash';
          const ref = isCash ? '' : acc.iban ? groupChars(acc.iban) : acc.accountNumber ? groupChars(acc.accountNumber) : '';
          return (
            <button
              key={acc.id} type="button" onClick={() => onPickAccount(acc.id)}
              className={`bank-acc${active ? ' on' : ''}`}
              title={active ? 'Showing this account. Click to show all accounts.' : `Show ${acc.name}'s transactions`}
            >
              <span className="bank-acc-head">
                <span className={`bank-acc-icon${isCash ? ' cash' : ''}`}>{isCash ? <IconCash size={16} /> : <IconBank size={16} />}</span>
                <span className="bank-acc-name">{acc.name}</span>
                {acc.isDefault && <span className="badge badge-primary" style={{ fontSize: 9.5, flexShrink: 0 }}>Default</span>}
              </span>
              <span className="bank-acc-bank">{isCash ? 'Cash in hand' : (acc.bankName || 'Bank account')}</span>
              {ref && <span className="bank-acc-ref" title={acc.iban ? 'IBAN' : 'Account number'}>{acc.iban ? 'IBAN ' : 'Acc '}{ref}</span>}
              {(() => {
                const f = flowByAccount[acc.id];
                return (
                  <span className="bank-acc-flow" title={`${monthLabel}: money in and out of ${acc.name}`}>
                    {txLoading ? <span className="skeleton" style={{ height: 12, width: 140, borderRadius: 4 }} /> : (<>
                      <span><span className="bank-flow-k">In</span><strong className={f?.in ? 'in' : 'zero'}>{fmtMoney(f?.in ?? 0, acc.currency)}</strong></span>
                      <span><span className="bank-flow-k">Out</span><strong className={f?.out ? 'out' : 'zero'}>{fmtMoney(f?.out ?? 0, acc.currency)}</strong></span>
                    </>)}
                  </span>
                );
              })()}
              <span className="bank-acc-foot">
                <span className={`bank-acc-bal${acc.currentBalance < 0 ? ' neg' : acc.currentBalance === 0 ? ' zero' : ''}`}>
                  {fmtMoney(acc.currentBalance, acc.currency)}
                </span>
                <span className="bank-total-cur">{acc.currency}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// The old Overview tab is gone — its content (balances per account and per
// currency) now lives in the always-visible summary bar above both tabs.
type BankTab = 'transactions' | 'accounts';

export default function BankPage() {
  const { user, business } = useAuth();
  const settings = useSettings();

  // ── Accounts ──────────────────────────────────────────────────────────────
  const [accounts,    setAccounts]    = useState<BankAccount[]>([]);
  const [accsLoading, setAccsLoading] = useState(true);

  // ── Transactions ──────────────────────────────────────────────────────────
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [txLoading,    setTxLoading]    = useState(false);
  const [filterYear,   setFilterYear]   = useState(now.getFullYear());
  const [filterMonth,  setFilterMonth]  = useState(now.getMonth());
  // A single day ('YYYY-MM-DD') picked from the date box — narrows the list
  // from the whole month to that day; '' = whole month.
  const [filterDay,    setFilterDay]    = useState('');
  const [currentPage,  setCurrentPage]  = useState(1);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [tab,        setTab]        = useState<BankTab>('transactions');
  const [modalMode,  setModalMode]  = useState<'add' | 'edit' | 'delete' | null>(null);
  const [selected,   setSelected]   = useState<BankAccount | null>(null);
  const [form,       setForm]       = useState<AccountForm>(() => emptyAccountForm(settings.currency));
  const [saving,     setSaving]     = useState(false);
  const [formError,  setFormError]  = useState('');
  const [entryOpen,  setEntryOpen]  = useState(false);
  const [payGstOpen, setPayGstOpen] = useState(false);
  const [moveKind,   setMoveKind]   = useState<BankMoveKind | null>(null);
  const [payKind,    setPayKind]    = useState<BankPaymentKind | null>(null);
  // Account cards folded to one line (per browser, remembered).
  const [summaryMini, setSummaryMini] = useState(() => {
    try { return localStorage.getItem('bank-summary-mini') === '1'; } catch { return false; }
  });
  const [delTx,      setDelTx]      = useState<BankTransaction | null>(null);
  const [delBusy,    setDelBusy]    = useState(false);

  // A sale payment / broker payout row opens its sale (Payments or Brokers
  // tab, with this payment picked out).
  const salePopup = useSalePopup();
  // An advance row's number opens where that advance was applied.
  const [advOpen, setAdvOpen] = useState<{ kind: 'customer' | 'supplier'; id: string } | null>(null);

  // What each payment row is about: the company and the invoice (or advance)
  // number, looked up from the record it points to (bank rows store only
  // its id). Used by the Party and Invoice No columns.
  const [refDocs, setRefDocs] = useState<{
    entities: Map<string, string>;
    sales: Map<string, { no: string; party: string }>;
    purchases: Map<string, { no: string; party: string }>;
    advances: Map<string, { no: string; party: string }>;
  }>({ entities: new Map(), sales: new Map(), purchases: new Map(), advances: new Map() });
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const put = (key: 'entities' | 'sales' | 'purchases', m: Map<string, never>) =>
      setRefDocs(r => ({ ...r, [key]: m }));
    const advs = { c: new Map<string, { no: string; party: string }>(), s: new Map<string, { no: string; party: string }>() };
    const unsubs = [
      onSnapshot(collection(db, 'entities'), snap => put('entities',
        new Map(snap.docs.map(d => [d.id, (d.data() as Entity).companyName])) as Map<string, never>), () => {}),
      onSnapshot(collection(db, 'sales'), snap => put('sales',
        new Map(snap.docs.map(d => { const x = d.data() as SaleInvoice; return [d.id, { no: x.invoiceNo, party: x.toEntityId }]; })) as Map<string, never>), () => {}),
      onSnapshot(collection(db, 'invoices'), snap => put('purchases',
        new Map(snap.docs.map(d => { const x = d.data() as PurchaseInvoice; return [d.id, { no: x.invoiceNo, party: x.fromEntityId }]; })) as Map<string, never>), () => {}),
      onSnapshot(collection(db, 'customerAdvances'), snap => {
        advs.c = new Map(snap.docs.map(d => { const x = d.data() as CustomerAdvance; return [d.id, { no: x.advanceNo, party: x.toEntityId }]; }));
        setRefDocs(r => ({ ...r, advances: new Map([...advs.c, ...advs.s]) }));
      }, () => {}),
      onSnapshot(collection(db, 'supplierAdvances'), snap => {
        advs.s = new Map(snap.docs.map(d => { const x = d.data() as SupplierAdvance; return [d.id, { no: x.advanceNo, party: x.fromEntityId }]; }));
        setRefDocs(r => ({ ...r, advances: new Map([...advs.c, ...advs.s]) }));
      }, () => {}),
    ];
    return () => unsubs.forEach(u => u());
  }, []);
  /** Party (company name) and record number behind a bank row; '' when it has none. */
  function txRef(tx: BankTransaction): { party: string; no: string; sale: boolean } {
    const id = tx.referenceId;
    const name = (eid?: string) => (eid ? refDocs.entities.get(eid) ?? '' : '');
    // A broker payout's description starts with the broker's name.
    const lead = tx.description?.split(' — (')[0] ?? '';
    if (id && tx.referenceType === 'ar_payment') { const d = refDocs.sales.get(id); if (d) return { party: name(d.party), no: d.no, sale: true }; }
    if (id && tx.referenceType === 'ap_payment') { const d = refDocs.purchases.get(id); if (d) return { party: name(d.party), no: d.no, sale: false }; }
    if (id && tx.referenceType === 'broker_payment') {
      const sd = refDocs.sales.get(id); if (sd) return { party: lead, no: sd.no, sale: true };
      const pd = refDocs.purchases.get(id); if (pd) return { party: lead, no: pd.no, sale: false };
    }
    if (id && (tx.referenceType === 'customer_advance' || tx.referenceType === 'supplier_advance')) {
      const d = refDocs.advances.get(id); if (d) return { party: name(d.party), no: d.no, sale: false };
    }
    return { party: '', no: '', sale: false };
  }
  const [openingTx, setOpeningTx] = useState('');
  async function openSaleFor(tx: BankTransaction) {
    if (!tx.referenceId) return;
    setOpeningTx(tx.id);
    await salePopup.open(tx.referenceId, { tab: tx.referenceType === 'broker_payment' ? 'brokers' : 'payments', bankTxId: tx.id });
    setOpeningTx('');
  }
  // /bank?tx=<id> (from a sale's payment): jump to that day and pick it out.
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingTx, setPendingTx] = useState(() => new URLSearchParams(location.search).get('tx') ?? '');
  const [hlTx, setHlTx] = useState('');

  // ── Load accounts ─────────────────────────────────────────────────────────
  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const unsub = onSnapshot(
      collection(db, 'bankAccounts'),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount));
        setAccounts(all.filter(a => a.isActive !== false).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
        setAccsLoading(false);
      },
      err => { console.error('bankAccounts listener error:', err); setAccsLoading(false); },
    );
    return unsub;
  }, [business]);

  // ── Load transactions ─────────────────────────────────────────────────────
  // Loaded on both tabs — the summary bar's month In/Out reads them too.
  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    setTxLoading(true);
    const unsub = onSnapshot(
      collection(db, 'bankTransactions'),
      snap => {
        setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankTransaction)));
        setTxLoading(false);
      },
      err => { console.error('bankTransactions listener error:', err); setTxLoading(false); },
    );
    return unsub;
  }, [business]);

  // ── Month navigation ──────────────────────────────────────────────────────
  // With a day picked, the arrows step one day; otherwise one month.
  function pickDay(day: string) {
    setFilterDay(day);
    if (day) { setFilterYear(Number(day.slice(0, 4))); setFilterMonth(Number(day.slice(5, 7)) - 1); }
  }
  function prevMonth() {
    if (filterDay) { pickDay(shiftDay(filterDay, -1)); return; }
    if (filterMonth === 0) { setFilterMonth(11); setFilterYear(y => y - 1); }
    else setFilterMonth(m => m - 1);
  }
  function nextMonth() {
    if (filterDay) { if (filterDay < localTodayISO()) pickDay(shiftDay(filterDay, 1)); return; }
    if (filterMonth === 11) { setFilterMonth(0); setFilterYear(y => y + 1); }
    else setFilterMonth(m => m + 1);
  }
  const isFutureMonth = filterYear > now.getFullYear() ||
    (filterYear === now.getFullYear() && filterMonth > now.getMonth());
  const isCurrentMonth = !filterDay && filterYear === now.getFullYear() && filterMonth === now.getMonth();
  function goToCurrentMonth() { setFilterDay(''); setFilterYear(now.getFullYear()); setFilterMonth(now.getMonth()); }
  const atLatest = filterDay ? filterDay >= localTodayISO() : isFutureMonth;
  const periodLabel = filterDay
    ? new Date(filterDay + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : `${MONTH_NAMES[filterMonth]} ${filterYear}`;

  useEffect(() => {
    if (new URLSearchParams(location.search).get('tx')) navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  useEffect(() => {
    if (!hlTx) return;
    const t = setTimeout(() => document.getElementById(`tx-${hlTx}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 150);
    return () => clearTimeout(t);
  }, [hlTx]);

  // ── Filtered transactions ─────────────────────────────────────────────────
  const periodTx = useMemo(() => {
    const monthPrefix = `${filterYear}-${String(filterMonth + 1).padStart(2, '0')}`;
    return transactions
      .filter(t => (filterDay ? t.date === filterDay : t.date?.startsWith(monthPrefix)))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [transactions, filterYear, filterMonth, filterDay]);

  // Excel-style filters on Type, Account and Category (replaced the header's
  // Account and Type menus). An account card click sets the Account filter.
  const accountName = (id: string) => accounts.find(a => a.id === id)?.name ?? '';
  const filterColumns = useMemo<FilterColumn<BankTransaction>[]>(() => [
    { key: 'type',     label: 'Type',     get: t => txTypeName(t) },
    { key: 'account',  label: 'Account',  get: t => t.accountName || accountName(t.accountId) },
    { key: 'category', label: 'Category', get: t => t.category },
    { key: 'party',    label: 'Party',    get: t => txRef(t).party },
    { key: 'invoice',  label: 'Invoice No', get: t => { const n = txRef(t).no; return n ? shortInvoiceNo(n) : ''; } },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [accounts, refDocs]);
  const { rows: visibleTx, filters: colFilters } = useColumnFilters(periodTx, filterColumns);
  // The account card that matches a one-account filter shows as picked.
  const pickedAccountNames = colFilters.selected.account;
  const activeAccountId = pickedAccountNames?.length === 1
    ? (accounts.find(a => a.name === pickedAccountNames[0])?.id ?? '') : '';
  function pickAccount(id: string) {
    const name = accountName(id);
    if (!name) return;
    colFilters.set('account', activeAccountId === id ? null : [name]);
    setTab('transactions');
  }

  // A new ?tx= while on the page (render time, no effect needed).
  const [lastSearch, setLastSearch] = useState(location.search);
  if (location.search !== lastSearch) {
    setLastSearch(location.search);
    const id = new URLSearchParams(location.search).get('tx');
    if (id) setPendingTx(id);
  }
  // Once that row is loaded, show its day and pick it out.
  if (pendingTx) {
    const t = transactions.find(x => x.id === pendingTx);
    if (t) {
      const [y, m] = t.date.split('-').map(Number);
      setTab('transactions'); colFilters.clear();
      setFilterYear(y); setFilterMonth(m - 1); setFilterDay(t.date);
      setHlTx(t.id); setPendingTx('');
    }
  }

  // This period's money in / out per account (shown in each account card).
  const flowByAccount = useMemo(() => {
    const monthPrefix = `${filterYear}-${String(filterMonth + 1).padStart(2, '0')}`;
    const out: Record<string, { in: number; out: number }> = {};
    for (const t of transactions) {
      if (!(filterDay ? t.date === filterDay : t.date?.startsWith(monthPrefix))) continue;
      const f = (out[t.accountId] ??= { in: 0, out: 0 });
      if (t.direction === 'in') f.in += t.amount; else f.out += t.amount;
    }
    return out;
  }, [transactions, filterYear, filterMonth, filterDay]);

  useEffect(() => { setCurrentPage(1); }, [filterYear, filterMonth, filterDay, colFilters.stamp]);
  // Days that have at least one transaction (under the account/type filters)
  // — the calendar marks them with a dot so empty days aren't a guess.
  const txDays = useMemo(() => new Set(transactions
    .map(t => t.date)), [transactions]);
  const totalPages = Math.max(1, Math.ceil(visibleTx.length / PAGE_SIZE));
  const pagedTx = visibleTx.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  // The table fits the screen: header pinned, rows scroll inside, Month
  // Total pinned at the bottom, page numbers just under it.
  const txWrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(txWrapRef, 56, 280, [visibleTx.length > 0, txLoading, tab]);

  // Grouped by currency — summing raw amounts across different currencies
  // (e.g. USD + INR) into one number would be meaningless, so each currency
  // gets its own Out/In/Net rather than one blended total.
  const txTotals = useMemo(() => {
    const byCurrency: Record<string, { out: number; in: number }> = {};
    visibleTx.forEach(t => {
      const bucket = byCurrency[t.currency] ?? (byCurrency[t.currency] = { out: 0, in: 0 });
      if (t.direction === 'out') bucket.out += t.amount;
      else bucket.in += t.amount;
    });
    return Object.entries(byCurrency)
      .map(([currency, { out, in: ins }]) => ({ currency, out, in: ins, net: ins - out }))
      .sort((a, b) => a.currency.localeCompare(b.currency));
  }, [visibleTx]);

  // ── Overview KPIs ─────────────────────────────────────────────────────────
  const overviewStats = useMemo(() => {
    const byCurrency: Record<string, number> = {};
    accounts.forEach(a => {
      byCurrency[a.currency] = (byCurrency[a.currency] ?? 0) + a.currentBalance;
    });
    return { accounts: accounts.length, byCurrency };
  }, [accounts]);

  // ── Account modal helpers ─────────────────────────────────────────────────
  function openAdd() {
    setForm(emptyAccountForm(settings.currency));
    setFormError(''); setSelected(null); setModalMode('add');
  }
  function openEdit(acc: BankAccount) {
    setSelected(acc);
    setForm({
      name: acc.name, type: acc.type, currency: acc.currency,
      bankName: acc.bankName ?? '', accountNumber: acc.accountNumber ?? '',
      ifscCode: acc.ifscCode ?? '', iban: acc.iban ?? '', swiftCode: acc.swiftCode ?? '',
      openingBalance: String(acc.openingBalance), notes: acc.notes ?? '',
    });
    setFormError(''); setModalMode('edit');
  }
  function closeModal() { setModalMode(null); setSelected(null); setFormError(''); }
  function setF<K extends keyof AccountForm>(k: K, v: AccountForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function validate(): string {
    if (!form.name.trim())     return 'Account name is required.';
    if (!form.currency)        return 'Currency is required.';
    if (form.type === 'bank' && !form.bankName.trim()) return 'Bank name is required.';
    const ob = parseFloat(form.openingBalance);
    if (isNaN(ob) || ob < 0)  return 'Opening balance must be 0 or more.';
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError('');
    const db = getClientDb();
    if (!db) { setFormError('Database not connected.'); setSaving(false); return; }
    const ob = parseFloat(form.openingBalance) || 0;
    const payload = {
      businessId:    business!.id,
      name:          form.name.trim(),
      type:          form.type,
      currency:      form.currency,
      bankName:      form.type === 'bank' ? form.bankName.trim()      : null,
      accountNumber: form.type === 'bank' ? form.accountNumber.trim() : null,
      ifscCode:      (form.type === 'bank' && form.currency === 'INR') ? form.ifscCode.trim().toUpperCase() : null,
      iban:          (form.type === 'bank' && form.currency !== 'INR') ? form.iban.trim().toUpperCase()     : null,
      swiftCode:     (form.type === 'bank' && form.currency !== 'INR') ? form.swiftCode.trim().toUpperCase(): null,
      openingBalance: ob, notes: form.notes.trim() || null,
      isDefault: false, isActive: true,
    };
    try {
      const n = new Date().toISOString();
      if (modalMode === 'add') {
        const accDoc = await addDoc(collection(db, 'bankAccounts'), {
          ...payload, currentBalance: ob, sortOrder: accounts.length, createdAt: n, createdBy: user!.id,
        });
        // Mirror the account into the chart of accounts so the ledger has
        // somewhere to post to (seedChartOfAccounts does this in bulk; a
        // freshly-created account needs its own).
        await setDoc(doc(db, 'ledgerAccounts', bankAccountSlug(accDoc.id)), stripUndef({
          id: bankAccountSlug(accDoc.id), businessId: business!.id,
          code: form.type === 'cash' ? '1005' : '1010',
          name: `${form.type === 'cash' ? 'Cash' : 'Bank'} – ${form.name.trim()}`,
          type: 'asset', normalBalance: 'debit', isSystem: true, isActive: true,
          currency: form.currency, linkedRefId: accDoc.id, sortOrder: 105, createdAt: n,
        }));
        if (ob > 0) {
          const btDoc = await addDoc(collection(db, 'bankTransactions'), {
            businessId: business!.id, accountId: accDoc.id, accountName: form.name.trim(),
            currency: form.currency, direction: 'in', amount: ob, date: todayISO(),
            description: 'Opening balance', referenceType: 'opening', createdAt: n, createdBy: user!.id,
          });
          // Ledger: Dr the new bank account / Cr Opening Balance Equity.
          const jId = entryId('opening_balance', btDoc.id);
          await setDoc(doc(db, 'journalEntries', jId), stripUndef({
            ...buildEntry({
              businessId: business!.id, sourceType: 'opening_balance', sourceId: btDoc.id,
              sourceCollection: 'bankTransactions', date: todayISO(), currency: form.currency,
              createdBy: user!.id, note: `Opening balance — ${form.name.trim()}`,
              lines: linesOpeningBank(ob, accDoc.id),
            }),
            id: jId,
          }));
        }
      } else if (selected) {
        await updateDoc(doc(db, 'bankAccounts', selected.id), {
          ...payload, currentBalance: selected.currentBalance + (ob - selected.openingBalance), openingBalance: ob,
        });
      }
      closeModal();
    } catch (e: unknown) { setFormError(e instanceof Error ? e.message : 'Failed to save.'); }
    setSaving(false);
  }

  async function handleDelete() {
    if (!selected) return;
    setSaving(true);
    const db = getClientDb();
    if (db) {
      try { await updateDoc(doc(db, 'bankAccounts', selected.id), { isActive: false }); closeModal(); }
      catch (e: unknown) { setFormError(e instanceof Error ? e.message : 'Failed to deactivate.'); }
    }
    setSaving(false);
  }

  const isAdd    = modalMode === 'add';
  const showIfsc = form.type === 'bank' && form.currency === 'INR';
  const showIban = form.type === 'bank' && form.currency !== 'INR';
  const sym      = getCurrencySymbol(form.currency);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout
      title="Bank"
      subtitle="Manage accounts, expenses and payment history"
      toolbar={<>
        {/* View first (Transactions / Accounts), then the Transactions filters. */}
        <div className="tb-seg" role="tablist" aria-label="View">
          {([
            { key: 'transactions' as const, label: 'Transactions' },
            { key: 'accounts' as const,     label: 'Accounts' },
          ]).map(({ key, label }) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key}
              className={tab === key ? 'on' : undefined} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
        {tab === 'transactions' && (<>
        {/* Month stepper — one joined control */}
        <div style={{ display: 'flex', alignItems: 'stretch', height: 34, border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', background: 'var(--surface)' }}>
          <button type="button" onClick={prevMonth} title={filterDay ? 'Previous day' : 'Previous month'} style={MONTH_STEP_BTN}><IconChevron dir="left" /></button>
          {/* Click the month → a calendar: browse months, pick one day,
              or take the whole month. */}
          <Dropdown
            trigger={open => (
              <button type="button" title="Pick a month or a specific day" style={{
                height: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '0 12px', width: 178, boxSizing: 'border-box', // fixed: changing the month never shifts the filters beside it
                justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit',
                border: 'none', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)',
                background: open || filterDay ? 'var(--primary-bg)' : 'transparent',
                fontSize: 12.5, fontWeight: 700, color: filterDay ? 'var(--primary)' : 'var(--text-1)', whiteSpace: 'nowrap',
              }}>
                <span style={{ color: 'var(--primary)', display: 'flex' }}><IconCalendar /></span>
                {periodLabel}
                <IconCaret />
              </button>
            )}
          >
            <MonthDayPicker
              year={filterYear} month={filterMonth} day={filterDay}
              txDays={txDays}
              onPickDay={pickDay}
              onPickMonth={(y, m) => { setFilterDay(''); setFilterYear(y); setFilterMonth(m); }}
            />
          </Dropdown>
          <button type="button" onClick={nextMonth} disabled={atLatest} title={filterDay ? 'Next day' : 'Next month'}
            style={{ ...MONTH_STEP_BTN, opacity: atLatest ? 0.35 : 1, cursor: atLatest ? 'default' : 'pointer' }}>
            <IconChevron dir="right" />
          </button>
        </div>

        {(colFilters.active.length > 0 || !isCurrentMonth) && (
          <button type="button" className="tb-clear" title="Back to this month and no column filters"
            onClick={() => { colFilters.clear(); goToCurrentMonth(); }}>
            Clear
          </button>
        )}
        </>)}
      </>}
      headerActions={tab === 'transactions' ? (
        <Dropdown
          align="right"
          trigger={() => (
            <Button variant="primary" size="sm" icon={<IconPlus />} style={{ height: 34, boxSizing: 'border-box' }}>New</Button>
          )}
        >
          <div className="dropdown-group-label">Against an invoice</div>
          <button type="button" className="dropdown-item" onClick={() => setPayKind('received')}>Payment received (customer)</button>
          <button type="button" className="dropdown-item" onClick={() => setPayKind('made')}>Payment made (supplier)</button>
          <div className="dropdown-divider" />
          <button type="button" className="dropdown-item" onClick={() => setEntryOpen(true)}>Expense</button>
          <button type="button" className="dropdown-item" onClick={() => setEntryOpen(true)}>Income</button>
          <div className="dropdown-divider" />
          <button type="button" className="dropdown-item" onClick={() => setMoveKind('transfer')}>Transfer between accounts</button>
          <button type="button" className="dropdown-item" onClick={() => setMoveKind('deposit')}>Deposit cash</button>
          <button type="button" className="dropdown-item" onClick={() => setMoveKind('withdrawal')}>Withdraw cash</button>
          <button type="button" className="dropdown-item" onClick={() => setMoveKind('charge')}>Bank charge</button>
          {isIndian(settings) && <>
            <div className="dropdown-divider" />
            <button type="button" className="dropdown-item" onClick={() => setPayGstOpen(true)}>Pay GST</button>
          </>}
        </Dropdown>
      ) : (
        <Button variant="primary" size="sm" icon={<IconPlus />} onClick={openAdd} style={{ height: 34, boxSizing: 'border-box' }}>Add Account</Button>
      )}
    >

      {/* ── Summary bar — every account's balance + this month's money in/out,
          always on top of both tabs. A card filters Transactions to that
          account; clicking it again shows all accounts. ── */}
      <BankSummaryBar
        accounts={accounts}
        loading={accsLoading}
        balancesByCurrency={overviewStats.byCurrency}
        monthLabel={periodLabel}
        flowByAccount={flowByAccount}
        txLoading={txLoading}
        activeAccountId={activeAccountId}
        onPickAccount={pickAccount}
        onAddAccount={() => { setTab('accounts'); openAdd(); }}
        collapsed={summaryMini}
        onToggle={() => setSummaryMini(v => { const n = !v; try { localStorage.setItem('bank-summary-mini', n ? '1' : '0'); } catch { /* private mode */ } return n; })}
      />


      {/* ── TRANSACTIONS ── */}
      {tab === 'transactions' && (
        <>
          {/* The Account filter shows on its selected card, so no chip for it. */}
          {colFilters.active.some(f => f.key !== 'account') && (
            <div className="no-print" style={{ display: 'flex', marginBottom: 8 }}>
              <ColumnFilterChips filters={colFilters} inline hide={['account']} />
            </div>
          )}
          {txLoading ? (
            <div className="table-wrap"><table>
              <thead><tr>{[80,100,80,120,80,140,100,80,80].map((w,i) => <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>)}</tr></thead>
              <tbody>{[...Array(4)].map((_,i) => <tr key={i} style={{ opacity: 1 - i*0.2 }}>{[70,90,70,110,70,120,90,70,70].map((w,j) => <td key={j}><div className="skeleton" style={{ height: 13, width: w, borderRadius: 4 }} /></td>)}</tr>)}</tbody>
            </table></div>
          ) : visibleTx.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon" style={{ display: 'flex', justifyContent: 'center', color: 'var(--text-3)' }}><IconTransactions size={32} /></div>
                <div className="empty-state-title">{periodTx.length > 0 ? 'Nothing matches these filters' : filterDay ? `No transactions on ${periodLabel}` : 'No transactions this month'}</div>
                <div className="empty-state-sub">Use <strong>New</strong> to record a payment against an invoice, an expense or income.</div>
              </div>
            </div>
          ) : (
            <>
              <div ref={txWrapRef} className="table-wrap table-fit table-fit-stats">
                <table className="bank-tx-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="th-filterable">Type<ColumnFilterButton filters={colFilters} colKey="type" label="Type" /></th>
                      <th className="th-filterable">Account<ColumnFilterButton filters={colFilters} colKey="account" label="Account" /></th>
                      <th className="th-filterable">Party<ColumnFilterButton filters={colFilters} colKey="party" label="Party" /></th>
                      <th className="th-filterable">Invoice No<ColumnFilterButton filters={colFilters} colKey="invoice" label="Invoice No" /></th>
                      <th>Details</th>
                      <th className="th-filterable">Category<ColumnFilterButton filters={colFilters} colKey="category" label="Category" /></th>
                      <th>Ref No</th>
                      <th style={{ textAlign: 'right', color: 'var(--danger)' }}>Out</th>
                      <th style={{ textAlign: 'right', color: 'var(--success)' }}>In</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTx.map(tx => {
                      const cs = tx.clearedStatus ?? 'uncleared';
                      const deletable = (['transfer', 'deposit', 'withdrawal', 'bank_charge', 'expense', 'income'] as string[])
                        .includes(tx.referenceType) && cs !== 'reconciled';
                      return (
                      <tr key={tx.id} id={`tx-${tx.id}`} className={tx.id === hlTx ? 'bank-tx-hl' : undefined}>
                        <td style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                          {new Date(tx.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <TxTypeBadge tx={tx} />
                            {cs === 'reconciled' && <span title="Reconciled" style={{ color: 'var(--success)', fontSize: 11 }}>✓</span>}
                            {cs === 'cleared' && <span title="Cleared" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warning)', display: 'inline-block' }} />}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          <span style={{ fontWeight: 500 }}>{tx.accountName}</span>
                          <span style={{ color: 'var(--text-3)', marginLeft: 4 }}>({tx.currency})</span>
                        </td>
                        {(() => {
                          const ref = txRef(tx);
                          const muted = <span style={{ opacity: 0.4 }}>—</span>;
                          return (<>
                            <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{ref.party || muted}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {ref.no ? (ref.sale ? (
                                <button type="button" className="sm-link" title={`Open ${ref.no}`} disabled={openingTx === tx.id}
                                  onClick={() => openSaleFor(tx)}><span className="sm-ref">{shortInvoiceNo(ref.no)}</span></button>
                              ) : (tx.referenceId && (tx.referenceType === 'customer_advance' || tx.referenceType === 'supplier_advance')) ? (
                                <button type="button" className="sm-link" title={`See where ${ref.no} was applied`}
                                  onClick={() => setAdvOpen({ kind: tx.referenceType === 'customer_advance' ? 'customer' : 'supplier', id: tx.referenceId! })}>
                                  <span className="sm-ref">{shortInvoiceNo(ref.no)}</span>
                                </button>
                              ) : <span className="sm-ref" title={ref.no}>{shortInvoiceNo(ref.no)}</span>) : muted}
                            </td>
                            {/* Payment rows say it all in Party + Invoice No; other rows keep their own text. */}
                            <td style={{ fontSize: 12 }}>
                              {(() => {
                                const text = ref.no ? tx.notes : tx.description;
                                return text ? <span className="cell-clip bank-tx-details" title={text}>{text}</span> : muted;
                              })()}
                            </td>
                          </>);
                        })()}
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{tx.category || '—'}</td>
                        <td style={{ fontFamily: 'var(--font-num)', fontSize: 11, color: 'var(--text-3)' }}>{tx.referenceNo || '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: tx.direction === 'out' ? 600 : 400, color: tx.direction === 'out' ? 'var(--danger)' : 'var(--text-3)' }}>
                          {tx.direction === 'out' ? fmtMoney(tx.amount, tx.currency) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: tx.direction === 'in' ? 600 : 400, color: tx.direction === 'in' ? 'var(--success)' : 'var(--text-3)' }}>
                          {tx.direction === 'in' ? fmtMoney(tx.amount, tx.currency) : '—'}
                        </td>
                        <td className="col-actions">
                          {deletable && <DeleteIconButton onClick={() => setDelTx(tx)} />}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                      <td colSpan={8} style={{ fontWeight: 600, fontSize: 12 }}>Month Total</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          {txTotals.some(t => t.out > 0) ? txTotals.filter(t => t.out > 0).map(t => (
                            <span key={t.currency} style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--danger)' }}>
                              {fmtMoney(t.out, t.currency)}
                            </span>
                          )) : <span style={{ color: 'var(--text-3)' }}>—</span>}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          {txTotals.some(t => t.in > 0) ? txTotals.filter(t => t.in > 0).map(t => (
                            <span key={t.currency} style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--success)' }}>
                              {fmtMoney(t.in, t.currency)}
                            </span>
                          )) : <span style={{ color: 'var(--text-3)' }}>—</span>}
                        </div>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {!txLoading && visibleTx.length > 0 && (
                <Pagination
                  current={currentPage} total={totalPages}
                  totalItems={visibleTx.length} pageSize={PAGE_SIZE}
                  onChange={setCurrentPage}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ── ACCOUNTS ── */}
      {tab === 'accounts' && (
        <>
          {accsLoading ? (
            <div className="table-wrap"><table>
              <thead><tr>{[120,70,70,100,100,100,80].map((w,i) => <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>)}</tr></thead>
              <tbody>{[...Array(3)].map((_,i) => <tr key={i}>{[100,60,60,90,90,90,60].map((w,j) => <td key={j}><div className="skeleton" style={{ height: 13, width: w, borderRadius: 4 }} /></td>)}</tr>)}</tbody>
            </table></div>
          ) : accounts.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon" style={{ display: 'flex', justifyContent: 'center', color: 'var(--text-3)' }}><IconBank size={32} /></div>
                <div className="empty-state-title">No accounts yet</div>
                <div className="empty-state-sub">Add a Cash account or your bank accounts to get started.</div>
              </div>
            </div>
          ) : (
            <div className="bank-detail-grid">
              {accounts.map(acc => {
                const isCash = acc.type === 'cash';
                const rows: [string, string | undefined, boolean?][] = isCash ? [] : [
                  ['Bank', acc.bankName],
                  ['Account No', acc.accountNumber ? groupChars(acc.accountNumber) : undefined, true],
                  ['IBAN', acc.iban ? groupChars(acc.iban) : undefined, true],
                  ['SWIFT / BIC', acc.swiftCode, true],
                  ['IFSC', acc.ifscCode, true],
                ];
                const shown = rows.filter(r => r[1]);
                return (
                  <div key={acc.id} className="bank-detail">
                    <div className="bank-acc-head">
                      <span className={`bank-acc-icon${isCash ? ' cash' : ''}`}>{isCash ? <IconCash size={16} /> : <IconBank size={16} />}</span>
                      <span className="bank-acc-name">{acc.name}</span>
                      {acc.isDefault && <span className="badge badge-primary" style={{ fontSize: 9.5 }}>Default</span>}
                      <span className="bank-detail-actions">
                        <Button variant="ghost" size="sm" icon={<IconEdit />} onClick={() => openEdit(acc)} title="Edit" />
                        <Button variant="ghost" size="sm" icon={<IconTrash />} style={{ color: 'var(--danger)' }}
                          onClick={() => { setSelected(acc); setFormError(''); setModalMode('delete'); }} title="Deactivate" />
                      </span>
                    </div>
                    <div className="bank-detail-meta">
                      <span className={`set-pill${isCash ? ' ok' : ''}`}>{isCash ? 'Cash' : 'Bank'}</span>
                      <span className="set-pill">{acc.currency}</span>
                    </div>
                    {shown.length > 0 ? (
                      <dl className="bank-detail-list">
                        {shown.map(([k, v, mono]) => (
                          <div key={k}><dt>{k}</dt><dd style={mono ? { fontFamily: 'var(--font-num)' } : undefined}>{v}</dd></div>
                        ))}
                      </dl>
                    ) : !isCash ? (
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No bank details yet. Edit to add them.</div>
                    ) : null}
                    <div className="bank-detail-foot">
                      <div><span className="bank-flow-k">Opening</span><span className="bank-detail-num">{fmtMoney(acc.openingBalance, acc.currency)}</span></div>
                      <div style={{ textAlign: 'right' }}>
                        <span className="bank-flow-k">Balance</span>
                        <span className={`bank-acc-bal${acc.currentBalance < 0 ? ' neg' : acc.currentBalance === 0 ? ' zero' : ''}`}>{fmtMoney(acc.currentBalance, acc.currency)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Add/Edit Account Modal ── */}
      <Modal
        open={modalMode === 'add' || modalMode === 'edit'}
        onClose={closeModal}
        hideHead
        size="md"
        footer={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {formError && <div className="msg msg-error" style={{ margin: 0 }}>{formError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>
                {isAdd ? 'Create Account' : 'Save Changes'}
              </Button>
            </div>
          </div>
        }
      >
        <ModalBanner
          title={form.name.trim() || (isAdd ? 'Add Account' : (selected?.name || 'Edit Account'))}
          eyebrow={isAdd ? (form.type === 'cash' ? 'New Cash Account' : 'New Bank Account') : 'Edit Account'}
          onClose={closeModal}
        />

        <div className="form-section">
          <div className="form-section-label">Account Type</div>
          <div className="ptype-seg" style={!isAdd ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            {(['cash', 'bank'] as BankAccountType[]).map(t => (
              <button key={t} type="button" className={`ptype-seg-btn${form.type === t ? ' active' : ''}`} onClick={() => setF('type', t)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {t === 'cash' ? <IconCash size={13} /> : <IconBank size={13} />}
                  {t === 'cash' ? 'Cash' : 'Bank Account'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-label">Account Details</div>
          <div className="form-row-2">
            <FormField label="Account Name" required>
              <input value={form.name} onChange={e => setF('name', e.target.value)}
                placeholder={form.type === 'cash' ? 'Cash' : 'HDFC Bank — INR'} />
            </FormField>
            <FormField label="Currency" required>
              <select value={form.currency} onChange={e => setF('currency', e.target.value)}>
                {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            </FormField>
          </div>

          {form.type === 'bank' && (
            <>
              <FormField label="Bank Name" required>
                <input value={form.bankName} onChange={e => setF('bankName', e.target.value)} placeholder="HDFC Bank / HSBC / ABN AMRO" />
              </FormField>
              <div className="form-row-2">
                <FormField label="Account Number">
                  <input value={form.accountNumber} onChange={e => setF('accountNumber', e.target.value)} placeholder="Last 4 digits fine" style={{ fontFamily: 'var(--font-num)' }} />
                </FormField>
                {showIfsc && (
                  <FormField label="IFSC Code" hint="Indian branch routing code">
                    <input value={form.ifscCode} onChange={e => setF('ifscCode', e.target.value.toUpperCase())} placeholder="HDFC0001234" style={{ fontFamily: 'var(--font-num)' }} />
                  </FormField>
                )}
                {showIban && (
                  <FormField label="IBAN">
                    <input value={form.iban} onChange={e => setF('iban', e.target.value.toUpperCase())} placeholder="DE89370400440532013000" style={{ fontFamily: 'var(--font-num)' }} />
                  </FormField>
                )}
              </div>
              {showIban && (
                <FormField label="SWIFT / BIC Code">
                  <input value={form.swiftCode} onChange={e => setF('swiftCode', e.target.value.toUpperCase())} placeholder="DEUTDEDB" style={{ fontFamily: 'var(--font-num)' }} />
                </FormField>
              )}
            </>
          )}

          <FormField label={`Opening Balance (${form.currency})`} hint={isAdd ? 'Current balance when setting up this account' : 'Changing this adjusts the current balance'}>
            <div className="input-wrap has-prefix">
              <span className="input-prefix">{sym}</span>
              <input type="number" min="0" step="0.01" value={form.openingBalance}
                onChange={e => setF('openingBalance', e.target.value)} placeholder="0.00" />
            </div>
          </FormField>

          <FormField label="Notes (optional)">
            <textarea value={form.notes} onChange={e => setF('notes', e.target.value)}
              placeholder="Any notes about this account…" style={{ minHeight: 52 }} />
          </FormField>
        </div>
      </Modal>

      {/* ── Deactivate Confirm ── */}
      <ConfirmModal
        open={modalMode === 'delete'}
        onClose={closeModal}
        onConfirm={handleDelete}
        title="Deactivate Account"
        message={<>Deactivate <strong>{selected?.name}</strong>? It will be hidden but all transactions are preserved.</>}
        confirmLabel="Deactivate"
        loading={saving}
        error={formError}
      />

      {/* ── Add Expense / Income Modal ── */}
      <AddEntryModal
        open={entryOpen}
        onClose={() => setEntryOpen(false)}
        accounts={accounts}
        defaultCurrency={settings.currency}
        userId={user!.id}
        businessId={business!.id}
      />

      {moveKind && (
        <BankMoveModal kind={moveKind} accounts={accounts} onClose={() => setMoveKind(null)} />
      )}
      {payGstOpen && <PayGstModal accounts={accounts} onClose={() => setPayGstOpen(false)} />}

      {delTx && (
        <ConfirmModal
          open
          title="Delete this transaction?"
          message={
            delTx.transferGroupId
              ? `This is one leg of a transfer — both legs and the ledger entry will be removed, and both account balances corrected.`
              : `"${delTx.description}" (${fmtMoney(delTx.amount, delTx.currency)}) will be removed and ${delTx.accountName}'s balance corrected.`
          }
          confirmLabel="Delete"
          loading={delBusy}
          onConfirm={() => handleDeleteTx(delTx)}
          onClose={() => setDelTx(null)}
        />
      )}
      {payKind && (
        <BankPaymentPicker
          kind={payKind} accounts={accounts}
          defaultAccountId={activeAccountId || undefined}
          onClose={() => setPayKind(null)}
        />
      )}
      {advOpen && (
        <AdvanceDetailModal
          kind={advOpen.kind} advanceId={advOpen.id}
          entityName={id => (id ? refDocs.entities.get(id) ?? '—' : '—')}
          onOpenInvoice={advOpen.kind === 'customer' ? (saleId => { salePopup.open(saleId, { tab: 'payments' }); }) : undefined}
          onClose={() => setAdvOpen(null)}
        />
      )}
      {salePopup.popup}
    </AppLayout>
  );

  async function handleDeleteTx(t: BankTransaction) {
    setDelBusy(true);
    const db = getClientDb();
    if (!db) { setDelBusy(false); return; }
    try {
      await runTransaction(db, async tx => {
        const logDelete = () => business && logActivity(tx, db, business.id, user, {
          module: 'bank', action: 'delete', recordId: t.id,
          note: `${txTypeName(t) || 'Entry'}: ${t.description || ''} · ${fmtMoney(t.amount, t.currency)} · ${t.accountName}`,
        });
        if (t.transferGroupId) {
          // Both legs share transferGroupId; the passed row is one of them.
          const legs = transactions.filter(x => x.transferGroupId === t.transferGroupId);
          const outLeg = legs.find(x => x.direction === 'out') ?? t;
          const inLeg  = legs.find(x => x.direction === 'in');
          const accRefs = [...new Set(legs.map(x => x.accountId))].map(id => doc(db, 'bankAccounts', id));
          const snaps = await Promise.all(accRefs.map(r => tx.get(r)));
          snaps.forEach((s, i) => {
            if (!s.exists()) return;
            const acc = accRefs[i];
            const bal = (s.data() as BankAccount).currentBalance;
            const isFrom = acc.id === outLeg.accountId;
            tx.update(acc, { currentBalance: isFrom ? bal + t.amount : bal - t.amount });
          });
          legs.forEach(x => tx.delete(doc(db, 'bankTransactions', x.id)));
          logDelete();
          tx.delete(doc(db, 'journalEntries', `bank_transfer__${t.transferGroupId}`));
          void inLeg;
        } else {
          const accRef = doc(db, 'bankAccounts', t.accountId);
          const s = await tx.get(accRef);
          if (s.exists()) {
            const bal = (s.data() as BankAccount).currentBalance;
            tx.update(accRef, { currentBalance: t.direction === 'in' ? bal - t.amount : bal + t.amount });
          }
          tx.delete(doc(db, 'bankTransactions', t.id));
          logDelete();
          const st =
            t.referenceType === 'deposit'     ? 'bank_deposit' :
            t.referenceType === 'withdrawal'  ? 'bank_withdrawal' :
            t.referenceType === 'bank_charge' ? 'bank_charge' :
            t.referenceType;   // 'expense' | 'income'
          tx.delete(doc(db, 'journalEntries', `${st}__${t.id}`));
        }
      });
      setDelTx(null);
    } catch {
      /* balances/onSnapshot self-correct; the row stays if it failed */
    }
    setDelBusy(false);
  }
}
